// The application step: FormV2 walks the dynamic middle of the form, and
// this component runs everything around it — the eligibility checkpoints
// during the walk, the end-of-form juncture (evaluate → can't-issue /
// confirm-updated-quote / upsell / pick-a-package → review), the directed
// SSN screen, and the completion. The renderer owns screen order and
// never auto-completes (externalReview); this harness acts only at the
// handoffs and never hardcodes the walk's shape beyond the named refs it
// is coupled to: the two checkpoint screens, the held SSN screen, and
// the review's edit targets.
//
// A COMPLETED application renders as a record instead: the same screens
// and the review, read-only, each carrying "your application is
// complete" with two ways on — Continue (forward to where the family
// stands) and Edit application (reopen: the server clones the completed
// response into a fresh in-progress walk, and every later milestone
// re-earns itself, re-signing included).

import { useEffect, useRef, useState } from "react";

import FormV2, {
  FormLoadingScreen,
  type FormV2Controls,
  type FormV2ExternalReview,
  type FormV2Hooks,
} from "components/FormV2/FormV2";
import type { FormV2Client } from "components/FormV2/api";
import { evaluateCondition } from "components/FormV2/conditions";
import type { V2ResolvedScreen } from "components/FormV2/types";
import Button from "components/Button";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import type { FormV2Event } from "components/FormV2/events";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { NetworkError } from "hooks/usePublicAPI";

import { useQuoteFlowAnalytics } from "./analytics";
import {
  confirmChange,
  confirmReview,
  decideUpsell,
  editApplication,
  evaluateQuote,
  recordSelection,
  type DeclineReason,
  type QuoteAcknowledgement,
  type QuoteEvaluation,
  type QuotePaymentOption,
  type QuotePlan,
  type QuoteSelection,
  type ScheduledCall,
} from "./api";
import type { QuoteFlags } from "./flags";
import {
  CantIssueModal,
  ChoosePlanScreen,
  ConfirmChangeScreen,
  OtherOptionsScreen,
  selectionDelta,
} from "./JunctureScreens";
import ScreenChrome, { NARROW_MAIN_CLASS } from "components/ScreenChrome";
import LoadingScreen from "./LoadingScreen";
import { hiddenBaselineFeatures, visiblePlans } from "./PricingCards";
import { singlePlanTreatment } from "./flags";
import { nameOf, sectionGroups } from "./review-answers";
import ReviewContent from "./ReviewContent";
import UpsellScreen from "./UpsellScreen";

// The named refs this harness is coupled to — the same coupling class as
// the form ref itself. The walk owns everything between them.
const SSN_SCREEN = "ssn";
const INSURED_DETAILS_SCREEN = "legal-basics";
const CARRIER_REQUIRED_SCREEN = "carrier-required-questions";
// Where "Back to the application" from the options screen re-enters: the
// walk's first body screen, so the family re-reads their answers from
// the top rather than landing past the hold.
const FIRST_BODY_SCREEN = INSURED_DETAILS_SCREEN;

// Every screen whose answers can move eligibility holds at its Continue:
// identity (date of birth, address) and the carrier-required set — the
// union health questions and policy replacement. The carrier-required
// screen is the flow's only health surface.
const CHECKPOINT_SCREENS = new Set([
  INSURED_DETAILS_SCREEN,
  CARRIER_REQUIRED_SCREEN,
]);

// Which checkpoint's answers each decline cause lives on. A hold fires
// only on the screen that can fix the cause — otherwise a family sent
// back to the first screen by a later-screen disqualifier could never
// walk forward to correct it. A cause the vocabulary can't place (an
// empty reasons list — config-shaped declines) holds everywhere; the
// end-of-form juncture stays the unconditional backstop either way.
const REASON_CHECKPOINTS: Record<DeclineReason["kind"], string> = {
  out_of_area: INSURED_DETAILS_SCREEN,
  age: INSURED_DETAILS_SCREEN,
  carrier_answers: CARRIER_REQUIRED_SCREEN,
  terminal: CARRIER_REQUIRED_SCREEN,
};

/** Every checkpoint standing on one verdict — the shape an ineligible
 * landing starts from. */
function checkpointStatesHolding(
  verdict: CheckpointVerdict,
): Record<string, CheckpointState> {
  const states: Record<string, CheckpointState> = {};
  for (const screenRef of CHECKPOINT_SCREENS) {
    states[screenRef] = { seq: 0, verdict, promise: null };
  }
  return states;
}

function holdsAt(verdict: CheckpointVerdict, screenRef: string): boolean {
  if (verdict.can_issue) return false;
  if (verdict.reasons.length === 0) return true;
  return verdict.reasons.some(
    (reason) => REASON_CHECKPOINTS[reason.kind] === screenRef,
  );
}

// Referentially stable — a new identity would re-derive the walk shape.
const EXTERNAL_REVIEW: FormV2ExternalReview = { heldScreens: [SSN_SCREEN] };

/** What the walk handed over when its body finished. */
interface BodyOutcome {
  screens: V2ResolvedScreen[];
  values: Record<string, unknown>;
  lastScreenRef: string | null;
}

/** Where the choose screen's Back returns: into the walk, to the review
 * that offered the change-plan link, or to the confirm screen whose "See
 * all packages" opened it — carrying what that screen needs to re-stand. */
type ChooseReturn =
  | { kind: "walk" }
  | { kind: "review" }
  | { kind: "confirm"; next: QuotePaymentOption; withdrawn: boolean };

type HarnessScreen =
  | { kind: "walking" }
  | { kind: "evaluating" }
  | {
      kind: "confirm";
      evaluation: QuoteEvaluation;
      body: BodyOutcome;
      next: QuotePaymentOption;
      withdrawn: boolean;
    }
  | {
      kind: "choose";
      evaluation: QuoteEvaluation;
      body: BodyOutcome;
      /** The Base upsell wears its own voice and keep-row; the
       * selection-less prompt is a plain pick. */
      upsell: boolean;
      returnTo: ChooseReturn;
    }
  | {
      kind: "review";
      evaluation: QuoteEvaluation;
      body: BodyOutcome;
      option: QuotePaymentOption;
    }
  | { kind: "juncture_error"; body: BodyOutcome }
  | { kind: "completing" }
  | {
      kind: "options";
      /** Null until an evaluation named the causes — the reload landing
       * starts generic and the background re-verify fills them in. */
      reasons: DeclineReason[] | null;
    };

/** The harness-owned faces as the analytics `screen` property names them.
 * "cant_issue" isn't a `HarnessScreen` kind (the can't-issue modal is a
 * `cantIssue` overlay, not a screen), so it's fired at its own call site
 * instead of through this mapping. The SSN screen is the renderer's and
 * reports itself (mirrored as `quote_flow_form_screen_viewed`). */
type HarnessScreenName =
  | "other_options"
  | "confirm_change"
  | "choose_plan"
  | "upsell"
  | "review"
  | "evaluation_error";

/** What each `HarnessScreen` reports as a `quote_flow_screen_viewed` —
 * null for the states that aren't a face at all (walking, evaluating,
 * completing). */
function screenFor(screen: HarnessScreen): {
  station: "application" | "review";
  screen: HarnessScreenName;
  reasons?: string[];
  delta_kind?: string;
} | null {
  switch (screen.kind) {
    case "options":
      return {
        station: "application",
        screen: "other_options",
        reasons: screen.reasons?.map((reason) => reason.kind),
      };
    case "confirm":
      return {
        station: "application",
        screen: "confirm_change",
        delta_kind: screen.withdrawn ? "option_withdrawn" : "price_changed",
      };
    case "choose":
      return {
        station: "application",
        screen: screen.upsell ? "upsell" : "choose_plan",
      };
    case "review":
      return { station: "review", screen: "review" };
    case "juncture_error":
      return { station: "application", screen: "evaluation_error" };
    default:
      return null;
  }
}

/** A stable key for the juncture-viewed effect's deps — narrower than
 * `screen` itself (a fresh object every render) but wide enough to
 * change whenever the actual juncture face changes, so a background
 * update to `reasons` alone (the ineligible landing's re-verify) never
 * re-fires the view it's still describing. */
function junctureScreenKey(screen: HarnessScreen): string {
  if (screen.kind === "confirm") return `confirm:${screen.withdrawn}`;
  if (screen.kind === "choose") return `choose:${screen.upsell}`;
  return screen.kind;
}

/** A checkpoint's answer: the verdict, and the causes when it said no. */
interface CheckpointVerdict {
  can_issue: boolean;
  reasons: DeclineReason[];
}

/** One checkpoint's verdict machinery: the save-sequence token, and the
 * prefetch keyed to it — a stale result discards harmlessly. */
interface CheckpointState {
  seq: number;
  verdict: CheckpointVerdict | null;
  promise: Promise<CheckpointVerdict | null> | null;
}

export function offeredFor(
  selection: QuoteSelection,
  plans: QuotePlan[],
): QuotePaymentOption | null {
  const plan = plans.find(
    (candidate) => candidate.slug === selection.plan_slug,
  );
  return (
    plan?.payment_options.find(
      (option) =>
        option.payment_period === selection.payment_period &&
        option.payment_period_oap === selection.payment_period_oap,
    ) ?? null
  );
}

// The loading faces' rotating status lines — honest, present-tense
// descriptions of the work underway. Module constants: a new identity
// would restart the rotation.
const EVALUATING_STATUSES = [
  "Checking your quote against your answers\u2026",
  "Confirming carrier availability\u2026",
  "Reviewing your payment options\u2026",
] as const;

const COMPLETING_STATUSES = [
  "Saving your answers\u2026",
  "Preparing your application for the carrier\u2026",
] as const;

// The walk's resume wait — one set of words held from the moment the
// flow starts loading until the walk renders, so a reload reads as one
// continuous moment.
const RESUME_LOADING_SCREEN = {
  section: "Your application",
  title: (
    <>
      Retrieving{" "}
      <span className="font-sans font-bold text-evergreen">your quote</span>
    </>
  ),
  description: "Loading your saved answers\u2026",
};

/** The application's loading look, exported so the flow can hold these
 * exact pixels while the view itself loads — the handoff into the walk's
 * own resume loader must not move anything. */
export function ApplicationLoadingScreen() {
  return (
    <main className={NARROW_MAIN_CLASS}>
      <FormLoadingScreen {...RESUME_LOADING_SCREEN} />
    </main>
  );
}

export interface ApplicationStepProps {
  quoteId: string;
  formRef: string;
  /** The base quote-credential client; this component wraps it to learn
   * the response id the completion call needs. Referentially stable. */
  client: FormV2Client;
  planningFor: "self" | "loved_one";
  /** The policy's market — the review localizes its testimonial. */
  marketSlug: string | null;
  flags: QuoteFlags | null;
  /** The application is completed and not reopened: everything renders
   * as a record with the Edit seam, never as a form. The host keys this
   * component on the mode, so a flip remounts cleanly. */
  applicationCompleted: boolean;
  /** The plans the view priced — the record's review face names the plan
   * and option from these. Null while pricing is unavailable. */
  pricedPlans: QuotePlan[] | null;
  /** Which of this component's two stations the URL names. */
  station: "application" | "review";
  /** The walk screen the URL names under /application, if any. */
  urlScreenRef: string | null;
  /** Moves the URL between the stations this component owns — push for
   * the family's own moves, replace for corrections. */
  onNavigateWithin: (
    target: { station: "application" | "review"; screenRef?: string | null },
    options?: { replace?: boolean },
  ) => void;
  /** The record's Continue: forward to where the family stands. */
  onContinueToCurrent: () => void;
  /** The application was reopened for editing — the host re-reads its
   * view, which is what flips this component back into the walk. */
  onReopened: () => void;
  /** The view's standing verdict: the last evaluation said no and no
   * answer is newer. The step lands on the options screen instead of the
   * walk, and a background re-verify self-heals a fixed record. */
  initialIneligible: boolean;
  initialSelection: QuoteSelection | null;
  initialAcknowledgements: QuoteAcknowledgement[];
  /** The upcoming booked call, when one exists — the selection-less screen
   * shows its reassurance, and speaks of "your call" only when true. */
  bookedCall: ScheduledCall | null;
  contactPhone: string | null;
  /** Opens the schedule-a-call modal — the faces' "talk to someone". */
  onScheduleCall: () => void;
  /** Fires as the step's content column changes width — the pricing-card
   * faces span the wide grid, everything else the form column — so the
   * flow bar above can stay flush with the content edges. */
  onContentWidthChange?: (width: "form" | "wide") => void;
  onExitToQuote: () => void;
  /** Fired once the walk's completion lands, carrying the selection the
   * junctures settled on — the flow's view predates them. */
  onDone: (selection: QuoteSelection | null) => void;
}

/** A new face reads from its top — no glide, the same instant jump the
 * walk uses between its screens. */
function snapToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

export default function ApplicationStep(props: ApplicationStepProps) {
  if (props.applicationCompleted) return <CompletedApplication {...props} />;
  return <ApplicationWalk {...props} />;
}

function ApplicationWalk({
  quoteId,
  formRef,
  client,
  planningFor,
  marketSlug,
  flags,
  initialIneligible,
  initialSelection,
  initialAcknowledgements,
  station,
  urlScreenRef,
  onNavigateWithin,
  bookedCall,
  contactPhone,
  onScheduleCall,
  onContentWidthChange,
  onExitToQuote,
  onDone,
}: ApplicationStepProps) {
  const { track, trackAtStep, reportFailure } = useQuoteFlowAnalytics();
  const [screen, setScreen] = useState<HarnessScreen>(
    initialIneligible
      ? { kind: "options", reasons: null }
      : { kind: "walking" },
  );
  // Every face change lands at the top, instantly — the walk snaps on its
  // own step changes, but the faces around it (the options screen, the
  // review, the junctures) otherwise open wherever the last one was
  // scrolled to, usually at its bottom button.
  useEffect(() => {
    snapToTop();
  }, [screen.kind]);
  // Handlers that fire from the walk underneath (onBodyComplete) read the
  // screen through a ref — the walk resumes regardless of what shows.
  const screenStateRef = useRef(screen);
  screenStateRef.current = screen;

  // ---- The URL and the walk keep each other honest. The walk's own
  // moves NAME the URL (each landed screen reports itself); the URL's own
  // moves — Back, Forward, a typed address — DRIVE the walk. One ref per
  // direction keeps them from echoing each other.
  //
  // Where the renderer currently stands, by ref — written from every
  // form_screen_viewed, the one cause-agnostic "landed here" signal.
  const walkScreenRef = useRef<string | null>(null);
  // True while the next landing was asked for BY the URL — that landing
  // corrects nothing and pushes nothing.
  const urlDrivenRef = useRef(false);
  // Whether the mount's URL-named screen was already honored — the first
  // settle re-enters it once; every later settle runs the juncture.
  const enteredFromUrlRef = useRef(false);
  // The first landing replaces rather than pushes: it is the walk taking
  // its place under the URL the family arrived on, not a move they made.
  const hasReportedRef = useRef(false);
  // The URL may only drive once the walk actually stands somewhere.
  const [walkSettled, setWalkSettled] = useState(false);
  /** The walk landed on a screen: the URL follows, unless the URL is what
   * sent it there. The very first landing defers to a URL that names a
   * DIFFERENT screen — the drive effect below honors that ask instead. */
  const reportWalkScreen = (screenRef: string | null) => {
    walkScreenRef.current = screenRef;
    if (!walkSettled) setWalkSettled(true);
    if (urlDrivenRef.current) {
      urlDrivenRef.current = false;
      return;
    }
    const first = !hasReportedRef.current;
    hasReportedRef.current = true;
    if (station === "application") {
      if (urlScreenRef === screenRef) return;
      if (first && urlScreenRef !== null && urlScreenRef !== SSN_SCREEN) {
        return;
      }
    }
    // Replacement only ever corrects an application-station URL (the
    // mount naming its landing screen); from any other station — review's
    // Continue opening the SSN screen — the landing is the family's own
    // move, and pushes.
    onNavigateWithin(
      { station: "application", screenRef },
      { replace: first && station === "application" },
    );
  };

  // Only the pricing-card faces span the wide grid.
  const contentWidth = screen.kind === "choose" ? "wide" : "form";
  useEffect(() => {
    onContentWidthChange?.(contentWidth);
  }, [contentWidth, onContentWidthChange]);

  // One `quote_flow_screen_viewed` per harness face actually landed
  // on — keyed on `junctureScreenKey`, not `screen` itself, so the
  // ineligible landing's background re-verify (which only fills in
  // `reasons` on the same "options" face) never re-fires the view.
  const junctureScreenKeyValue = junctureScreenKey(screen);
  const reportedJunctureKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // StrictMode runs a mount effect twice; a landing on an already
    // ineligible quote starts on a juncture face and would report it twice.
    if (reportedJunctureKeyRef.current === junctureScreenKeyValue) return;
    reportedJunctureKeyRef.current = junctureScreenKeyValue;
    const face = screenFor(screen);
    if (!face) return;
    const { station: faceStation, ...properties } = face;
    trackAtStep(faceStation, "quote_flow_screen_viewed", properties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [junctureScreenKeyValue]);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [cantIssue, setCantIssue] = useState<
    | { context: "checkpoint"; reasons: DeclineReason[] }
    | {
        context: "juncture";
        lastScreenRef: string | null;
        reasons: DeclineReason[];
      }
    | null
  >(null);

  // The can't-issue modal is an overlay independent of `screen` — its own
  // boolean-keyed effect so opening it (from either a checkpoint hold or
  // the end-of-form juncture) always reports exactly once.
  const cantIssueOpen = cantIssue !== null;
  useEffect(() => {
    if (cantIssue) {
      track("quote_flow_screen_viewed", {
        screen: "cant_issue",
        reasons: cantIssue.reasons.map((reason) => reason.kind),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantIssueOpen]);

  const controlsRef = useRef<FormV2Controls>(null);
  const bodyRef = useRef<BodyOutcome | null>(null);
  // The agreement state, read through refs by the async juncture so a
  // just-written selection is never shadowed by a stale closure.
  const selectionRef = useRef<QuoteSelection | null>(initialSelection);
  const acknowledgementsRef = useRef<QuoteAcknowledgement[]>(
    initialAcknowledgements,
  );
  // An ineligible landing starts with the server's "no" standing at every
  // checkpoint (causes unknown until the re-verify names them), so the walk
  // re-entered from the options screen holds at Continue instead of
  // sailing past an unchanged disqualifier.
  const checkpointsRef = useRef<Record<string, CheckpointState>>(
    initialIneligible
      ? checkpointStatesHolding({ can_issue: false, reasons: [] })
      : {},
  );
  /** A verdict learned outside a checkpoint's own prefetch — the landing's
   * re-verify, the end-of-form juncture — becomes every checkpoint's
   * standing verdict. The next required save invalidates it like any other. */
  const rememberVerdict = (verdict: CheckpointVerdict) => {
    for (const screenRef of CHECKPOINT_SCREENS) {
      const state = (checkpointsRef.current[screenRef] ??= {
        seq: 0,
        verdict: null,
        promise: null,
      });
      state.verdict = verdict;
      state.promise = null;
    }
  };

  const recordAgreement = (result: {
    selection: QuoteSelection;
    acknowledgement: QuoteAcknowledgement;
  }) => {
    selectionRef.current = result.selection;
    acknowledgementsRef.current = [
      result.acknowledgement,
      ...acknowledgementsRef.current,
    ];
  };

  const facePlans = (plans: QuotePlan[]): QuotePlan[] =>
    flags ? visiblePlans(plans, flags) : plans;
  const faceIncluded = (
    plans: QuotePlan[],
    shown: QuotePlan[],
  ): QuotePlan["features"] | null =>
    flags ? hiddenBaselineFeatures(plans, shown) : null;
  /** Whether this face shows one package as the plan. Every affordance
   * that exists to change package answers to this: with one on offer
   * there is nothing to change to, and offering the change anyway is
   * the comparison the treatment removes, reappearing later in the
   * flow. `facePlans` already answers for unresolved flags by returning
   * everything the server priced, so this needs no null guard of its
   * own — and must not have one, or a market priced for a single
   * package would render that package in a grid built for three. */
  const faceIsSolo = (plans: QuotePlan[]): boolean =>
    singlePlanTreatment(facePlans(plans));

  /** After a screen's write moved the selection (and possibly the settled
   * variant), the walk re-reads: new unanswered screens put the customer
   * back in it; otherwise it settles on body_complete and the handoff
   * re-fires — which re-runs this juncture against the new state. */
  const resumeWalkAfterSelectionChange = async () => {
    const controls = controlsRef.current;
    if (!controls) return;
    const { standing } = await controls.refresh();
    if (standing.kind === "body") setScreen({ kind: "walking" });
    // body_complete re-fires onBodyComplete; nothing more to do here.
  };

  /** Every juncture settle lands on review — natural walk, edit round
   * trip, or reload, review-confirmed or not. The SSN screen is only
   * ever entered through review's own Continue, so "Save & return to
   * review" always means it, and a reload while standing on review or
   * SSN re-lands on review (one extra Looks-right press, by design —
   * simpler than remembering how the walk arrived here). */
  const showReview = (evaluation: QuoteEvaluation, body: BodyOutcome): void => {
    const selection = selectionRef.current;
    const option = selection
      ? offeredFor(selection, evaluation.pricing.plans)
      : null;
    if (!selection || !option) {
      // The chain only lands here with a live, offered selection; losing
      // it mid-flight reads as a fresh pick.
      setScreen({
        kind: "choose",
        evaluation,
        body,
        upsell: false,
        returnTo: { kind: "walk" },
      });
      return;
    }
    setScreen({ kind: "review", evaluation, body, option });
  };

  const decideScreens = (evaluation: QuoteEvaluation, body: BodyOutcome) => {
    rememberVerdict({
      can_issue: evaluation.can_issue,
      reasons: evaluation.reasons,
    });
    if (!evaluation.can_issue) {
      setScreen({ kind: "walking" });
      setCantIssue({
        context: "juncture",
        lastScreenRef: body.lastScreenRef,
        reasons: evaluation.reasons,
      });
      return;
    }
    const plans = evaluation.pricing.plans;
    const selection = selectionRef.current;
    // A rep seed was never the family's own choice — the selection-less
    // path ends in the pick, with the seed preselected.
    if (!selection || selection.source === "rep") {
      setScreen({
        kind: "choose",
        evaluation,
        body,
        upsell: false,
        returnTo: { kind: "walk" },
      });
      return;
    }
    const delta = selectionDelta(selection, plans);
    if (delta.kind === "plan_unavailable") {
      setScreen({
        kind: "choose",
        evaluation,
        body,
        upsell: false,
        returnTo: { kind: "walk" },
      });
      return;
    }
    if (delta.kind === "option_withdrawn") {
      if (delta.fallback === null) {
        setScreen({
          kind: "choose",
          evaluation,
          body,
          upsell: false,
          returnTo: { kind: "walk" },
        });
        return;
      }
      setScreen({
        kind: "confirm",
        evaluation,
        body,
        next: delta.fallback,
        withdrawn: true,
      });
      return;
    }
    if (delta.kind === "price_changed") {
      setScreen({
        kind: "confirm",
        evaluation,
        body,
        next: delta.offered,
        withdrawn: false,
      });
      return;
    }
    // Unchanged. The Base upsell shows once per QUOTE — one decision,
    // whatever selections follow it. A later confirm appends a new
    // selection row, and re-opening a question the family already
    // answered would read as a sales loop.
    if (
      selection.plan_slug === "simple" &&
      // The upsell's whole content is other packages to switch to.
      !faceIsSolo(evaluation.pricing.plans) &&
      !acknowledgementsRef.current.some((ack) => ack.kind === "upsell_decided")
    ) {
      setScreen({
        kind: "choose",
        evaluation,
        body,
        upsell: true,
        returnTo: { kind: "walk" },
      });
      return;
    }
    showReview(evaluation, body);
  };

  const runJuncture = async (body: BodyOutcome) => {
    setScreen({ kind: "evaluating" });
    setProblem(null);
    try {
      const evaluation = await evaluateQuote(quoteId);
      decideScreens(evaluation, body);
    } catch (error) {
      reportFailure("evaluate_quote", error);
      setScreen({ kind: "juncture_error", body });
    }
  };

  /** The review rebuilt from a fresh server read — for a pop that landed
   * on /review while the walk stands elsewhere, where the renderer's own
   * settle never re-fires. Reads the resolved answers the same way the
   * record's review does, then runs the ordinary juncture over them, so
   * what stands is whatever the fresh facts demand: the review with
   * current values, a re-priced confirmation, or the walk again. */
  const recheckReview = async () => {
    setScreen({ kind: "evaluating" });
    setProblem(null);
    try {
      const resumed = await client.resume();
      const resolved = await client.fetchResolvedView(resumed.form_response.id);
      const values = Object.fromEntries(
        resolved.answers
          .filter((answer) => !answer.stale)
          .map((answer) => [answer.field_ref, answer.value]),
      );
      const scope = {
        answers: values,
        context: resolved.form_response.resolved_context,
        variables: resolved.variables,
      };
      const screens = resolved.screens.filter(
        (screen) =>
          screen.kind === "body" &&
          screen.ref !== SSN_SCREEN &&
          (screen.visible_when === null ||
            evaluateCondition(screen.visible_when, scope)),
      );
      const body: BodyOutcome = {
        screens,
        values,
        lastScreenRef: screens.at(-1)?.ref ?? null,
      };
      bodyRef.current = body;
      await runJuncture(body);
    } catch (error) {
      reportFailure("recheck_review", error);
      setScreen({
        kind: "juncture_error",
        body: bodyRef.current ?? {
          screens: [],
          values: {},
          lastScreenRef: null,
        },
      });
    }
  };

  const enterSsn = (
    evaluation: QuoteEvaluation,
    body: BodyOutcome,
    option: QuotePaymentOption,
  ) => {
    void (async () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const landed = await controls.goToScreen(SSN_SCREEN, {
        directed: {
          onContinue: () => void completeApplication(),
          onBack: () => setScreen({ kind: "review", evaluation, body, option }),
        },
      });
      if (landed.ok) {
        // The SSN screen is the renderer's — it reports its own view. A
        // RE-entry emits no screen_viewed (the renderer never left the
        // step), so the landing reports by hand; a fresh entry's viewed
        // event covers it and the by-hand call would double, so it is
        // gated on the renderer already standing there.
        setScreen({ kind: "walking" });
        if (walkScreenRef.current === SSN_SCREEN) {
          reportWalkScreen(SSN_SCREEN);
        }
      } else {
        reportFailure(
          "enter_ssn",
          new Error("The walk could not open the SSN screen"),
        );
        setScreen({ kind: "review", evaluation, body, option });
        setProblem(
          "We couldn't open the last step — give it another try in a moment.",
        );
      }
    })();
  };

  // Completion goes through the renderer's own control — the harness never
  // addresses a forms endpoint itself. The renderer emits the
  // form_submitted / form_completed / form_completion_refused beats; this
  // side only decides where the walk goes on each outcome.
  const completeApplication = async () => {
    const controls = controlsRef.current;
    if (!controls) return;
    setScreen({ kind: "completing" });
    let outcome: Awaited<ReturnType<FormV2Controls["complete"]>>;
    try {
      outcome = await controls.complete();
    } catch (error) {
      reportFailure("complete_application", error);
      recoverFromRefusedCompletion();
      return;
    }
    if (outcome.ok) {
      onDone(selectionRef.current);
      return;
    }
    if (outcome.status === 404) {
      // The response vanished underneath the submit. The renderer's own
      // reload recovery already owns what happens next — it lands on a
      // fresh response (and re-fires onBodyComplete if that walk is done)
      // or on its not-found face. Re-running the juncture here would only
      // put a stale face in front of that.
      setProblem(null);
      setScreen({ kind: "walking" });
      return;
    }
    // A refusal — the juncture's own criteria moved under the submit — is
    // expected, and the re-verify is exactly the backstop for it.
    recoverFromRefusedCompletion();
  };

  const recoverFromRefusedCompletion = () => {
    setProblem(
      "We couldn't submit the application just yet — let's double-check things.",
    );
    const body = bodyRef.current;
    if (body) void runJuncture(body);
    else setScreen({ kind: "walking" });
  };

  const directedEdit = (screenRef: string) => {
    track("quote_flow_screen_action", { screen: "review", action: "edit" });
    void (async () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const landed = await controls.goToScreen(screenRef, {
        directed: {
          continueTo: "body_complete",
          continueLabel: "Save & return to review",
        },
      });
      if (landed.ok) {
        setScreen({ kind: "walking" });
        return;
      }
      reportFailure(
        "review_edit",
        new Error("The walk could not open the section to edit"),
      );
      setProblem(
        "We couldn't open that section — give it another try in a moment.",
      );
    })();
  };

  const backIntoWalk = (lastScreenRef: string | null) => {
    void (async () => {
      const controls = controlsRef.current;
      if (!controls || !lastScreenRef) return;
      const landed = await controls.goToScreen(lastScreenRef);
      if (landed.ok) {
        setScreen({ kind: "walking" });
        // Same by-hand report as the SSN entry: re-entering the screen
        // the renderer already stands on emits no screen_viewed.
        if (walkScreenRef.current === lastScreenRef) {
          reportWalkScreen(lastScreenRef);
        }
      }
    })();
  };

  // ---- The eligibility checkpoints (R16): a required save invalidates
  // every cached verdict (the evaluation is a whole-quote fact); the save
  // completing a checkpoint screen's required set prefetches; forward
  // exit holds for a current-token verdict; errors fail open — the end
  // juncture is the unconditional backstop. That fail-open is deliberate
  // and permanent (not a missed spot): a broken prefetch here costs
  // nothing but a moment's latency, so neither catch below reports to
  // Sentry the way the end-of-form `runJuncture` does.
  const onFieldSaved: FormV2Hooks["onFieldSaved"] = ({
    screenRef,
    required,
    screenRequiredComplete,
  }) => {
    if (!screenRef || !CHECKPOINT_SCREENS.has(screenRef) || !required) return;
    for (const state of Object.values(checkpointsRef.current)) {
      state.seq += 1;
      state.verdict = null;
      state.promise = null;
    }
    const state = (checkpointsRef.current[screenRef] ??= {
      seq: 0,
      verdict: null,
      promise: null,
    });
    if (!screenRequiredComplete) return;
    const seq = state.seq;
    state.promise = evaluateQuote(quoteId).then(
      (evaluation) => {
        if (state.seq !== seq) return null;
        const verdict = {
          can_issue: evaluation.can_issue,
          reasons: evaluation.reasons,
        };
        state.verdict = verdict;
        return verdict;
      },
      () => null,
    );
  };

  const checkpointVerdict = async (
    screenRef: string,
  ): Promise<CheckpointVerdict | null> => {
    const state = checkpointsRef.current[screenRef];
    if (state?.verdict) return state.verdict;
    if (state?.promise) return state.promise;
    try {
      const evaluation = await evaluateQuote(quoteId);
      return { can_issue: evaluation.can_issue, reasons: evaluation.reasons };
    } catch {
      return null;
    }
  };

  const onBeforeStepChange: FormV2Hooks["onBeforeStepChange"] = async ({
    from,
    kind,
  }) => {
    if (kind !== "advance" || from.kind !== "body") return "proceed";
    const screenRef = from.screenRef;
    if (!screenRef || !CHECKPOINT_SCREENS.has(screenRef)) return "proceed";
    // No entry means no verdict is standing and nothing on this screen
    // was edited this session — Continue owes no re-check; the
    // end-of-form juncture stays the unconditional backstop. A standing
    // "no" (an ineligible landing, a juncture refusal) or any required
    // edit here creates the entry, and the entry decides.
    if (!checkpointsRef.current[screenRef]) return "proceed";
    const verdict = await checkpointVerdict(screenRef);
    if (verdict !== null && holdsAt(verdict, screenRef)) {
      setCantIssue({ context: "checkpoint", reasons: verdict.reasons });
      return "hold";
    }
    return "proceed";
  };

  // The renderer already emits each of these under its generic name; the
  // harness mirrors every one as quote_flow_form_<same> with the quote's
  // base props stamped on, so the funnel reads a single event family.
  // The screen-viewed beat doubles as the walk's position report — the
  // one cause-agnostic "landed here" signal the URL follows.
  const onEvent = (event: FormV2Event) => {
    if (event.type === "form_screen_viewed" && event.screen_kind === "body") {
      reportWalkScreen(event.screen_ref ?? null);
    }
    const { type, ...props } = event;
    track(`quote_flow_${type}`, props);
  };

  const hooks: FormV2Hooks = {
    onFieldSaved,
    onBeforeStepChange,
    onBackFromFirstScreen: onExitToQuote,
    onEvent,
    onBodyComplete: (outcome) => {
      bodyRef.current = outcome;
      // Settling on body_complete is as much "the walk stands somewhere"
      // as landing on a screen — the URL may drive from here (a Back into
      // the walk from the review this settle is about to show).
      setWalkSettled(true);
      // The ineligible landing holds the options screen while the walk
      // resumes (fully answered, it settles here immediately) — leaving
      // is deliberate: the family's Back, or the re-verify's heal.
      if (screenStateRef.current.kind === "options") return;
      // A URL naming a walk screen is the family's stated position — a
      // reload there, or an Edit taken from there — and outranks the
      // settle's auto-juncture, once, and ONLY when the settle came
      // straight from the resume (no screen was ever reported): a settle
      // after the family moved is their own Continue, and bouncing it
      // back to the screen they just left would dead-press the button.
      if (
        !enteredFromUrlRef.current &&
        !hasReportedRef.current &&
        station === "application" &&
        urlScreenRef !== null &&
        urlScreenRef !== SSN_SCREEN
      ) {
        enteredFromUrlRef.current = true;
        urlDrivenRef.current = true;
        backIntoWalk(urlScreenRef);
        return;
      }
      enteredFromUrlRef.current = true;
      void runJuncture(outcome);
    },
  };

  // The ineligible landing's background re-verify: the stored verdict can
  // outlive its facts (a rep fixed the record), and one fresh evaluation
  // either fills in the causes or heals the landing back into the flow.
  useEffect(() => {
    if (!initialIneligible) return;
    let cancelled = false;
    evaluateQuote(quoteId).then(
      (evaluation) => {
        if (cancelled || screenStateRef.current.kind !== "options") return;
        rememberVerdict({
          can_issue: evaluation.can_issue,
          reasons: evaluation.reasons,
        });
        if (!evaluation.can_issue) {
          setScreen({ kind: "options", reasons: evaluation.reasons });
          return;
        }
        const body = bodyRef.current;
        if (body) void runJuncture(body);
        else setScreen({ kind: "walking" });
      },
      (error) => {
        // Could not re-verify — the stored verdict stands, generically.
        // Still worth an engineer's eyes: a persistently broken re-verify
        // means an ineligible landing never heals or learns its causes.
        reportFailure("evaluate_quote", error);
      },
    );
    return () => {
      cancelled = true;
    };
    // Mount-only by design: the landing happens once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The review face is a station of its own: landing on it names the
  // URL. Leaving it back into the walk, the screens name themselves
  // through reportWalkScreen — nothing to do here.
  const screenKind = screen.kind;
  useEffect(() => {
    if (screenKind !== "review") return;
    if (station !== "review") {
      onNavigateWithin(
        { station: "review" },
        { replace: urlDrivenRef.current },
      );
    }
    urlDrivenRef.current = false;
    // Compared at fire time; the URL props must not re-fire a landing
    // they themselves reflect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenKind]);

  // The URL leads, the walk follows: a Back/Forward pop (or a typed
  // address) re-places the walk onto the screen or face the URL names.
  // The walk's own moves already named the URL, so an in-sync ask does
  // nothing; the transient faces (evaluating, junctures) settle on their
  // own and correct the URL when they land.
  useEffect(() => {
    if (!walkSettled) return;
    const current = screenStateRef.current;
    if (station === "review") {
      if (
        current.kind === "review" ||
        current.kind === "evaluating" ||
        current.kind === "completing"
      ) {
        return;
      }
      // A pop landed on /review while something else stands. The cached
      // settle cannot be trusted — the family may have edited answers on
      // the way here (Back, edit, Forward skips the Continue that would
      // re-read them) — so the review is rebuilt from a fresh read, the
      // loading screen showing honestly while it loads.
      void recheckReview();
      return;
    }
    if (urlScreenRef === SSN_SCREEN && walkScreenRef.current !== SSN_SCREEN) {
      // The SSN screen opens only through review's own Continue — a cold
      // ask for it re-lands on review, the same place a reload lands.
      onNavigateWithin({ station: "review" }, { replace: true });
      return;
    }
    if (
      current.kind === "review" ||
      current.kind === "confirm" ||
      current.kind === "choose" ||
      current.kind === "juncture_error" ||
      current.kind === "options"
    ) {
      // Back from a standing face into the walk. The settled faces sit
      // over the walk's last screen with no URL of their own, so a pop
      // names a screen and the face gives way to it — the juncture that
      // raised the face re-runs when the walk completes again. Only the
      // in-flight faces (evaluating, completing) ride out a pop. (The
      // options landing's hold in onBodyComplete is untouched: that
      // guards the walk SETTLING under the face, not the family's Back.)
      urlDrivenRef.current = true;
      backIntoWalk(
        urlScreenRef ??
          (current.kind === "options"
            ? walkScreenRef.current
            : current.body.lastScreenRef),
      );
      return;
    }
    if (current.kind !== "walking") return;
    if (urlScreenRef !== null && urlScreenRef !== walkScreenRef.current) {
      urlDrivenRef.current = true;
      void (async () => {
        const controls = controlsRef.current;
        if (!controls) return;
        let landed = await controls.goToScreen(urlScreenRef);
        if (!landed.ok) {
          // The one race worth absorbing: the ask can beat the walk's own
          // load by a breath — one short retry before deciding the ref is
          // genuinely unknown.
          await new Promise((resolve) => setTimeout(resolve, 400));
          landed =
            (await controlsRef.current?.goToScreen(urlScreenRef)) ?? landed;
        }
        if (!landed.ok) {
          // Unknown, hidden, or refused — the URL corrects to where the
          // walk actually stands.
          urlDrivenRef.current = false;
          onNavigateWithin(
            { station: "application", screenRef: walkScreenRef.current },
            { replace: true },
          );
        }
      })();
    }
    // Driven by the URL alone — the walk's own state is read through refs
    // so a face change never re-runs the drive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station, urlScreenRef, walkSettled]);

  const renderHarnessScreen = () => {
    switch (screen.kind) {
      case "walking":
        return null;
      case "evaluating":
        return (
          <LoadingScreen
            kicker="Your application"
            titleA="Checking"
            titleB="your quote"
            statuses={EVALUATING_STATUSES}
          />
        );
      case "completing":
        return (
          <LoadingScreen
            kicker="Your application"
            titleA="Saving"
            titleB="your application"
            statuses={COMPLETING_STATUSES}
          />
        );
      case "options":
        return (
          <OtherOptionsScreen
            reasons={screen.reasons}
            onBackToApplication={() => {
              track("quote_flow_screen_action", {
                screen: "other_options",
                action: "back_to_application",
              });
              void (async () => {
                const controls = controlsRef.current;
                if (!controls) return;
                const landed = await controls.goToScreen(FIRST_BODY_SCREEN);
                if (landed.ok) setScreen({ kind: "walking" });
              })();
            }}
            onTypeformClicked={() =>
              track("quote_flow_screen_action", {
                screen: "other_options",
                action: "typeform_clicked",
              })
            }
          />
        );
      case "juncture_error":
        return (
          <ScreenChrome
            kicker="Your application"
            titleA="We couldn't check"
            titleB="your quote"
            blurb="Something got in the way of re-checking your quote against your answers. Nothing you entered is lost."
          >
            <div className="mt-8">
              <button
                type="button"
                className={CONTINUE_BUTTON_CLASS}
                onClick={() => {
                  track("quote_flow_screen_action", {
                    screen: "evaluation_error",
                    action: "retry",
                  });
                  void runJuncture(screen.body);
                }}
              >
                Try again
                <ChevronRightIcon
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              </button>
            </div>
          </ScreenChrome>
        );
      case "confirm": {
        const selection = selectionRef.current;
        if (!selection) return null;
        return (
          <ConfirmChangeScreen
            planName={
              screen.evaluation.pricing.plans.find(
                (plan) => plan.slug === selection.plan_slug,
              )?.name ?? selection.plan_slug
            }
            selection={selection}
            delta={
              screen.withdrawn
                ? {
                    kind: "option_withdrawn",
                    fallback: screen.next,
                  }
                : { kind: "price_changed", offered: screen.next }
            }
            busy={busy}
            problem={problem}
            onBackToAnswers={() => backIntoWalk(screen.body.lastScreenRef)}
            seeAllLabel={
              faceIsSolo(screen.evaluation.pricing.plans)
                ? "See all options"
                : undefined
            }
            onSeeAllPackages={() => {
              // The action name stays `see_all_packages` whatever the
              // button reads, so the existing series does not break at
              // the point the wording changes.
              track("quote_flow_screen_action", {
                screen: "confirm_change",
                action: "see_all_packages",
              });
              setScreen({
                kind: "choose",
                evaluation: screen.evaluation,
                body: screen.body,
                upsell: false,
                returnTo: {
                  kind: "confirm",
                  next: screen.next,
                  withdrawn: screen.withdrawn,
                },
              });
            }}
            onConfirm={(option) => {
              void (async () => {
                setBusy(true);
                setProblem(null);
                try {
                  const result = await confirmChange(quoteId, {
                    plan_slug: selection.plan_slug,
                    option_slug: option.slug,
                  });
                  recordAgreement(result);
                  track("quote_flow_screen_action", {
                    screen: "confirm_change",
                    action: "continue",
                  });
                  track("quote_flow_selection_confirmed", {
                    plan_slug: selection.plan_slug,
                    option_slug: option.slug,
                    source: "confirm_change",
                    queued: false,
                  });
                  await resumeWalkAfterSelectionChange();
                } catch (error) {
                  reportFailure("confirm_change", error);
                  setProblem(
                    "We couldn't save that — give it another try in a moment.",
                  );
                } finally {
                  setBusy(false);
                }
              })();
            }}
          />
        );
      }
      case "choose": {
        const shown = facePlans(screen.evaluation.pricing.plans);
        const solo = faceIsSolo(screen.evaluation.pricing.plans);
        const selection = selectionRef.current;
        if (screen.upsell) {
          return (
            <UpsellScreen
              plans={shown}
              currentOption={
                selection
                  ? offeredFor(selection, screen.evaluation.pricing.plans)
                  : null
              }
              busy={busy}
              problem={problem}
              onKeep={() => {
                void (async () => {
                  setBusy(true);
                  setProblem(null);
                  try {
                    recordAgreement(
                      await decideUpsell(quoteId, { outcome: "kept" }),
                    );
                    track("quote_flow_screen_action", {
                      screen: "upsell",
                      action: "keep_current",
                    });
                    showReview(screen.evaluation, screen.body);
                  } catch (error) {
                    reportFailure("decide_upsell", error);
                    setProblem(
                      "We couldn't save that — give it another try in a moment.",
                    );
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
              onUpgrade={(choice) => {
                void (async () => {
                  setBusy(true);
                  setProblem(null);
                  try {
                    recordAgreement(
                      await decideUpsell(quoteId, {
                        outcome: "upgraded",
                        plan_slug: choice.plan_slug,
                        option_slug: choice.option_slug,
                      }),
                    );
                    track("quote_flow_screen_action", {
                      screen: "upsell",
                      action: "upgrade",
                    });
                    track("quote_flow_selection_confirmed", {
                      plan_slug: choice.plan_slug,
                      option_slug: choice.option_slug,
                      source: "upsell",
                      queued: false,
                    });
                    await resumeWalkAfterSelectionChange();
                  } catch (error) {
                    reportFailure("decide_upsell", error);
                    setProblem(
                      "We couldn't save that selection — give it another try in a moment.",
                    );
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
              onBack={() => backIntoWalk(screen.body.lastScreenRef)}
            />
          );
        }
        return (
          <ChoosePlanScreen
            kicker="Your quote"
            titleA={solo ? "Choose how" : "Pick the package"}
            titleB={solo ? "you'd like to pay" : "that fits"}
            // "Your call" is only a thing to say when one is booked;
            // otherwise the band below carries the phone story.
            blurb={
              solo
                ? bookedCall
                  ? "Your answers are saved, and this price reflects them. Choose how you'd like to pay to keep going — or hold off and decide together on your call."
                  : "Your answers are saved, and this price reflects them. Choose how you'd like to pay to keep going."
                : bookedCall
                  ? "Your answers are saved, and these prices reflect them. Choose a package and payment option to keep going — or hold off and decide together on your call."
                  : "Your answers are saved, and these prices reflect them. Choose a package and payment option to keep going."
            }
            plans={shown}
            includedInAllFeatures={faceIncluded(
              screen.evaluation.pricing.plans,
              shown,
            )}
            singlePlan={solo}
            initialSelection={
              selection
                ? {
                    plan_slug: selection.plan_slug,
                    option_slug:
                      offeredFor(selection, screen.evaluation.pricing.plans)
                        ?.slug ?? "",
                  }
                : null
            }
            phoneBand={{ bookedCall, contactPhone, onSchedule: onScheduleCall }}
            busy={busy}
            problem={problem}
            onBackToAnswers={() => {
              const returnTo = screen.returnTo;
              if (returnTo.kind === "review") {
                showReview(screen.evaluation, screen.body);
                return;
              }
              if (returnTo.kind === "confirm") {
                setScreen({
                  kind: "confirm",
                  evaluation: screen.evaluation,
                  body: screen.body,
                  next: returnTo.next,
                  withdrawn: returnTo.withdrawn,
                });
                return;
              }
              backIntoWalk(screen.body.lastScreenRef);
            }}
            onChoose={(choice) => {
              void (async () => {
                setBusy(true);
                setProblem(null);
                try {
                  const recorded = await recordSelection(quoteId, {
                    ...choice,
                    source: "make_selection",
                  });
                  selectionRef.current = recorded.selection;
                  track("quote_flow_screen_action", {
                    screen: "choose_plan",
                    action: "choose",
                  });
                  track("quote_flow_selection_confirmed", {
                    plan_slug: choice.plan_slug,
                    option_slug: choice.option_slug,
                    source: "choose_plan",
                    queued: false,
                  });
                  await resumeWalkAfterSelectionChange();
                } catch (error) {
                  reportFailure("record_selection", error);
                  setProblem(
                    "We couldn't save that selection — give it another try in a moment.",
                  );
                } finally {
                  setBusy(false);
                }
              })();
            }}
          />
        );
      }
      case "review": {
        const selection = selectionRef.current;
        if (!selection) return null;
        const plan = screen.evaluation.pricing.plans.find(
          (candidate) => candidate.slug === selection.plan_slug,
        );
        return (
          <ScreenChrome
            kicker="Your application"
            titleA="Let's check"
            titleB="everything over"
            blurb="This is what goes on the policy, exactly as it reads."
          >
            <div className="mt-7">
              <ReviewContent
                planName={plan?.name ?? selection.plan_slug}
                planFeatures={plan?.features ?? []}
                option={screen.option}
                planningFor={planningFor}
                insuredName={nameOf(screen.body.values.insured_name)}
                marketSlug={marketSlug}
                sections={sectionGroups(
                  screen.body.screens,
                  screen.body.values,
                )}
                onChangePlan={
                  faceIsSolo(screen.evaluation.pricing.plans)
                    ? undefined
                    : () => {
                        track("quote_flow_screen_action", {
                          screen: "review",
                          action: "change_plan",
                        });
                        setScreen({
                          kind: "choose",
                          evaluation: screen.evaluation,
                          body: screen.body,
                          upsell: false,
                          returnTo: { kind: "review" },
                        });
                      }
                }
                onChangeSection={directedEdit}
              />
            </div>
            {problem && (
              <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
                {problem}
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <button
                type="button"
                className="bg-transparent p-0 font-bold text-evergreen underline underline-offset-4 transition-colors hover:text-plum"
                disabled={busy}
                onClick={() => backIntoWalk(screen.body.lastScreenRef)}
              >
                Back
              </button>
              <Button
                variant="continue"
                isLoading={busy}
                icon={
                  <ChevronRightIcon
                    className="h-4 w-4"
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                }
                iconPosition="right"
                onClick={() => {
                  void (async () => {
                    setBusy(true);
                    setProblem(null);
                    try {
                      track("quote_flow_review_submitted");
                      recordAgreement(await confirmReview(quoteId));
                      track("quote_flow_review_confirmed");
                      enterSsn(screen.evaluation, screen.body, screen.option);
                    } catch (error) {
                      track("quote_flow_review_failed", {
                        status:
                          error instanceof NetworkError
                            ? error.statusCode
                            : null,
                      });
                      reportFailure("confirm_review", error);
                      setProblem(
                        "We couldn't save that — give it another try in a moment.",
                      );
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                Continue
              </Button>
            </div>
          </ScreenChrome>
        );
      }
    }
  };

  const screenContent = renderHarnessScreen();
  return (
    <>
      <div className={screenContent !== null ? "hidden" : undefined}>
        <main className={NARROW_MAIN_CLASS}>
          <FormV2
            ref={controlsRef}
            formRef={formRef}
            client={client}
            skipStartScreen
            externalReview={EXTERNAL_REVIEW}
            hooks={hooks}
            loadingScreen={RESUME_LOADING_SCREEN}
          />
        </main>
      </div>
      {screenContent}
      {cantIssue && (
        <CantIssueModal
          reasons={cantIssue.reasons}
          onClose={() => setCantIssue(null)}
          onSeeOtherOptions={() => {
            track("quote_flow_screen_action", {
              screen: "cant_issue",
              action: "see_other_options",
            });
            const reasons = cantIssue.reasons;
            setCantIssue(null);
            setScreen({ kind: "options", reasons });
          }}
          onUpdateAnswers={() => {
            track("quote_flow_screen_action", {
              screen: "cant_issue",
              action: "update_answers",
            });
            // At a checkpoint they are already standing on the answers;
            // from the juncture the walk re-enters where it left off.
            const target =
              cantIssue.context === "juncture" ? cantIssue.lastScreenRef : null;
            setCantIssue(null);
            // The modal opened over a screen scrolled to its Continue; the
            // answers to update are above.
            snapToTop();
            if (target !== null) backIntoWalk(target);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// The completed application, rendered as a record
// ---------------------------------------------------------------------

/** The record's standing actions, under every read-only face: the fact,
 * the way forward, and the way back into editing. Edit reopens the
 * application server-side (a fresh in-progress copy of the completed
 * response) and hands control to the host, whose re-read flips this
 * whole component back into the walk. */
function CompletedApplicationActions({
  quoteId,
  face,
  onContinueToCurrent,
  onReopened,
}: {
  quoteId: string;
  /** Which read-only face carries these actions, for the event stream. */
  face: "application_record" | "review_record";
  onContinueToCurrent: () => void;
  onReopened: () => void;
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const [reopening, setReopening] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const reopen = () => {
    void (async () => {
      setReopening(true);
      setProblem(null);
      try {
        track("quote_flow_screen_action", {
          screen: face,
          action: "edit_application",
        });
        await editApplication(quoteId);
        // Still "reopening" on purpose: the host's re-read remounts this
        // component into the walk, and the button must not re-arm first.
        onReopened();
      } catch (error) {
        reportFailure("edit_application", error);
        setProblem(
          "We couldn't reopen the application — give it another try in a moment.",
        );
        setReopening(false);
      }
    })();
  };

  return (
    <div className="mt-8">
      <p className="rounded-xl border border-oat-300 bg-oat-100 px-5 py-4 text-base leading-relaxed text-oat-700">
        Your application is complete — these answers are what goes on the
        policy. To change something, reopen it with Edit application; your
        answers stay put, and you&rsquo;ll re-sign the paperwork at the end.
      </p>
      {problem && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          {problem}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <button
          type="button"
          className="bg-transparent p-0 font-bold text-evergreen underline underline-offset-4 transition-colors hover:text-plum disabled:opacity-60"
          disabled={reopening}
          aria-busy={reopening || undefined}
          onClick={reopen}
        >
          {reopening ? "Reopening…" : "Edit application"}
        </button>
        <button
          type="button"
          className={CONTINUE_BUTTON_CLASS}
          disabled={reopening}
          onClick={() => {
            track("quote_flow_screen_action", {
              screen: face,
              action: "continue_to_current",
            });
            onContinueToCurrent();
          }}
        >
          Continue
          <ChevronRightIcon
            className="h-4 w-4"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
}

function CompletedApplication(props: ApplicationStepProps) {
  if (props.station === "review") return <CompletedReview {...props} />;
  return <CompletedWalkScreens {...props} />;
}

/** The walk's screens as a record: FormV2 read-only, the URL naming the
 * screen and Back/Forward moving between them, the standing actions
 * underneath. */
function CompletedWalkScreens({
  quoteId,
  formRef,
  client,
  urlScreenRef,
  onNavigateWithin,
  onContinueToCurrent,
  onReopened,
  onContentWidthChange,
}: ApplicationStepProps) {
  const { track } = useQuoteFlowAnalytics();
  const controlsRef = useRef<FormV2Controls>(null);
  const walkScreenRef = useRef<string | null>(null);
  const urlDrivenRef = useRef(false);
  const hasReportedRef = useRef(false);
  const [walkSettled, setWalkSettled] = useState(false);

  useEffect(() => {
    onContentWidthChange?.("form");
  }, [onContentWidthChange]);

  const onEvent = (event: FormV2Event) => {
    if (event.type === "form_screen_viewed" && event.screen_kind === "body") {
      walkScreenRef.current = event.screen_ref ?? null;
      setWalkSettled(true);
      if (urlDrivenRef.current) {
        urlDrivenRef.current = false;
      } else {
        const first = !hasReportedRef.current;
        hasReportedRef.current = true;
        const landed = event.screen_ref ?? null;
        const deferToUrlAsk =
          first && urlScreenRef !== null && urlScreenRef !== landed;
        if (!deferToUrlAsk && urlScreenRef !== landed) {
          onNavigateWithin(
            { station: "application", screenRef: landed },
            { replace: first },
          );
        }
      }
    }
    const { type, ...props } = event;
    track(`quote_flow_${type}`, props);
  };
  const hooksRef = useRef<FormV2Hooks>({ onEvent });
  hooksRef.current = { onEvent };

  // The URL leads: Back/Forward (or a typed address) re-places the record
  // onto the screen it names; an unknown ask corrects to where it stands.
  useEffect(() => {
    if (!walkSettled) return;
    if (urlScreenRef === null || urlScreenRef === walkScreenRef.current) {
      return;
    }
    urlDrivenRef.current = true;
    void (async () => {
      const controls = controlsRef.current;
      if (!controls) return;
      let landed = await controls.goToScreen(urlScreenRef);
      if (!landed.ok) {
        // Same race the walk absorbs: one short retry before deciding
        // the ref is genuinely unknown.
        await new Promise((resolve) => setTimeout(resolve, 400));
        landed =
          (await controlsRef.current?.goToScreen(urlScreenRef)) ?? landed;
      }
      if (!landed.ok) {
        urlDrivenRef.current = false;
        onNavigateWithin(
          { station: "application", screenRef: walkScreenRef.current },
          { replace: true },
        );
      }
    })();
    // The walk's own position is read through refs; only the URL drives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlScreenRef, walkSettled]);

  return (
    <main className={NARROW_MAIN_CLASS}>
      <FormV2
        ref={controlsRef}
        formRef={formRef}
        client={client}
        skipStartScreen
        readOnly
        externalReview={EXTERNAL_REVIEW}
        hooks={hooksRef.current}
        loadingScreen={RESUME_LOADING_SCREEN}
      />
      {walkSettled && (
        <CompletedApplicationActions
          quoteId={quoteId}
          face="application_record"
          onContinueToCurrent={onContinueToCurrent}
          onReopened={onReopened}
        />
      )}
    </main>
  );
}

/** The review as a record: the same layout the live review uses, with the
 * edit affordances gone and the standing actions underneath. Reads the
 * completed response's frozen resolution directly — no walk to mount. */
function CompletedReview({
  quoteId,
  client,
  planningFor,
  marketSlug,
  pricedPlans,
  initialSelection,
  onContinueToCurrent,
  onReopened,
  onContentWidthChange,
}: ApplicationStepProps) {
  const { reportFailure } = useQuoteFlowAnalytics();
  const [record, setRecord] = useState<{
    screens: V2ResolvedScreen[];
    values: Record<string, unknown>;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    onContentWidthChange?.("form");
  }, [onContentWidthChange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resumed = await client.resume();
        const resolved = await client.fetchResolvedView(
          resumed.form_response.id,
        );
        if (cancelled) return;
        const values = Object.fromEntries(
          resolved.answers
            .filter((answer) => !answer.stale)
            .map((answer) => [answer.field_ref, answer.value]),
        );
        const scope = {
          answers: values,
          context: resolved.form_response.resolved_context,
          variables: resolved.variables,
        };
        setRecord({
          // The review reads the walk's own pages: visible body screens,
          // minus the held SSN screen — the live review never shows it
          // either, and a record must not print it.
          screens: resolved.screens.filter(
            (screen) =>
              screen.kind === "body" &&
              screen.ref !== SSN_SCREEN &&
              (screen.visible_when === null ||
                evaluateCondition(screen.visible_when, scope)),
          ),
          values,
        });
      } catch (error) {
        if (!cancelled) {
          reportFailure("read_completed_application", error);
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, reportFailure]);

  if (failed) {
    return (
      <ScreenChrome
        kicker="Your application"
        titleA="We couldn't open"
        titleB="your application"
        blurb="Something got in the way of reading your saved answers. Reload to try again — nothing is lost."
      >
        {null}
      </ScreenChrome>
    );
  }

  const selection = initialSelection;
  const plan =
    selection && pricedPlans
      ? pricedPlans.find((candidate) => candidate.slug === selection.plan_slug)
      : undefined;
  const option =
    selection && pricedPlans ? offeredFor(selection, pricedPlans) : null;
  // A completed application always carries a settled selection, and its
  // policy prices — a record that cannot name its plan is a read failure
  // in practice, and reads as one.
  if (record !== null && (!selection || !plan || !option)) {
    return (
      <ScreenChrome
        kicker="Your application"
        titleA="We couldn't open"
        titleB="your application"
        blurb="Something got in the way of reading your saved answers. Reload to try again — nothing is lost."
      >
        {null}
      </ScreenChrome>
    );
  }

  return (
    <ScreenChrome
      kicker="Your application"
      titleA="Everything you"
      titleB="told us"
      blurb="This is what goes on the policy, exactly as it reads."
    >
      {record === null || !selection || !plan || !option ? (
        // The chrome above already carries the screen's heading — while
        // the saved answers load, only a quiet spinner stands where the
        // content will, never a second heading the loaded screen lacks.
        <div
          className="mt-7 flex justify-center py-16"
          role="status"
          aria-label="Loading your saved answers"
        >
          <span className="block h-8 w-8 animate-spin rounded-full border-[3px] border-oat-300 border-t-evergreen" />
        </div>
      ) : (
        <div className="mt-7">
          <ReviewContent
            planName={plan.name}
            planFeatures={plan.features}
            option={option}
            planningFor={planningFor}
            insuredName={nameOf(record.values.insured_name)}
            marketSlug={marketSlug}
            sections={sectionGroups(record.screens, record.values)}
          />
        </div>
      )}
      <CompletedApplicationActions
        quoteId={quoteId}
        face="review_record"
        onContinueToCurrent={onContinueToCurrent}
        onReopened={onReopened}
      />
    </ScreenChrome>
  );
}
